import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Accordion, Button, Checkbox, Icon, Label, Tab, Table, Transition } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { ChangesWrapper, StyledAccordion } from './style'
import dayjs from 'dayjs'
import i18n from '~/components/i18n'
import ShowInfo from './ShowInfo'
import cloneDeep from 'lodash.clonedeep'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import FarmersTradingTable from './FarmersTradingTable'
import { useAuth } from '~/routes/components/AuthProvider'
import Dialog from '~/components/Dialog'
import {
  ADMIN_ROLE_SLUG,
  FARMER_MANAGER_ROLE_SLUG,
  IFE_ROLE_SLUG,
  LIGHT_USER_ROLE_SLUG,
  OPERATIVE_ROLE_SLUG,
  PRICE_CHANGE_STATUS_SLUGS
} from '~/Constants'
import TableComponent from '~/components/TableComponent'
import { formatNumber, formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'
import CustomInput from '~/components/CustomInput'
import ExcelExport from '~/components/ExcelExport'
import PlanModal from '~/components/PlanModal'
import NotifyModal from './NotifyModal'
import StockExChangeService from '~/api/services/stockExchange'

const getColumnsChangePreview = () => [
  {
    name: i18n.t('Value changed'),
    key: 'title'
  },
  {
    name: i18n.t('Trading month'),
    key: 'tradingMonth',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Farmer group/Farmer'),
    key: 'farmerGroup'
  },
  {
    name: i18n.t('Changes'),
    key: 'changes',
    type: 'changes'
  }
]

const ShowStockExchange: React.FC = () => {
  const { t } = useTranslation()
  const {
    systemData: {
      logged: { roleSlug },
      operativeUsersCanSeeFarmersInfo,
      amountUnit,
      amountVolume,
      amountPrice
    }
  } = useAuth()
  const { id } = useParams()

  const [isBusy, setIsBusy] = useState(false)
  const [showData, setShowData] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [farmerGroupTradingData, setFarmerGroupTradingData] = useState([])
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [trackChanges, setTrackChanges] = useState([])
  const [isTradingSlotExpired, setIsTradingSlotExpired] = useState(false)
  const [openPlanModal, setOpenPlanModal] = useState(false)
  const [openNotifyModal, setOpenNotifyModal] = useState(false)
  const [hash, updateHash] = useState()

  const columnsChangePreview = getColumnsChangePreview()
  const disableActionRole = (roleSlug === LIGHT_USER_ROLE_SLUG) || (roleSlug === FARMER_MANAGER_ROLE_SLUG)
  const operativeUserLogged = roleSlug === OPERATIVE_ROLE_SLUG
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  dayjs.extend(utc)
  dayjs.extend(timezone)

  const tradingMonthsTableHeaders = [
    {
      title: i18n.t('Trading month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      title: i18n.t('Trading amount (in %)'),
      key: 'percent',
      type: 'input'
    },
    {
      title: i18n.t('Trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'monthlyTradingAmount',
      type: 'number'
    },
    {
      title: i18n.t('BLE price {{priceLabel}}', { priceLabel }),
      key: 'price',
      type: 'input'
    },
    {
      title: i18n.t('ife price'),
      key: 'ifePrice',
      type: 'icon'
    },
    {
      title: i18n.t('Deactivated'),
      key: 'status',
      type: 'icon'
    }
  ]

  const handleHashAdd = useCallback((showData) => {
    updateHash(showData?.status || '')
  }, [showData])

  useEffect(() => {
    fetchShowData()
  }, [])

  useEffect(() => {
    return () => {
      handleHashAdd(showData);
    };
  }, [showData, handleHashAdd]);

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const showResponse = await StockExChangeService.showTradingSlot(id)
      const responseSettings = await StockExChangeService.getTradingSlotsFarmerSettings()
      const now = dayjs(new Date())
      const endDate = dayjs(showResponse.data.dateTo)
      setIsTradingSlotExpired(now.diff(endDate) > 0)
      setShowData(
        {
          ...showResponse.data,
          displayIfe: Boolean(responseSettings.data?.ifeSubmitPrices)
        })
      setTrackChanges([])
      setActiveTabIndex(0)
      setFarmerGroupTradingData([])
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function displayNotify() {
    const { status, showDefinedTradingSlots } = showData
    return !isTradingSlotExpired && (status === 'defined') && showDefinedTradingSlots === 1
  }

  function renderActions() {
    return <>
      <ExcelExport
        url={`stock-exchange/trading-slots/${id}/export`}
        fileName={'stock-exchange'}
      />
      {!isTradingSlotExpired && showData?.status === 'defined' && <El.PageHeaderActionButton
        onClick={() => setOpenPlanModal(true)}
        size='large'
      >{t('Plan')}</El.PageHeaderActionButton>}
      {showData && displayNotify() && <El.PageHeaderActionButton
        onClick={() => setOpenNotifyModal(true)}
        size='large'
      >{t('Notify farmers')}</El.PageHeaderActionButton>}
    </>
  }

  function handleDeactivated(value, index) {
    const idToShow = showData.farmerGroups[activeTabIndex - 1].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.show.id === idToShow)
    const tempFarmersGroup = cloneDeep(farmerGroupTradingData)
    tempFarmersGroup[indexPlaced].tradingMonths[index].status = value ? 0 : 1
    handleTrackChanges('status', value ? 0 : 1, index, t('Status'), indexPlaced)
  }

  function renderStatusCellValue(value, index) {
    const ifeLogged = roleSlug === IFE_ROLE_SLUG
    return isTradingSlotExpired ? <Icon color={value === 1 ? 'red' : 'green'} name={value === 1 ? 'close' : 'check'} /> : <Checkbox
      checked={value === 0}
      disabled={(disableActionRole || ifeLogged) || showData?.status === 'active'}
      onChange={(e, data) => handleDeactivated(data.checked, index)}
    />
  }

  function getUnit(key) {
    switch (key) {
      case 'percent':
        return '%'
      case 'price':
        return priceLabel
      default:
        return ''
    }
  }

  function getValue(key, value) {
    switch (key) {
      case 'percent':
      case 'price':
        return value ? `${formatNumber(value.replace(',', '.'))}` : t('Not defined')
      default:
        return value
    }
  }

  function isOldEqualNewValue(newValue, oldValue, key) {
    switch (key) {
      case 'percent':
      case 'price':
        return formatNumber(newValue.replace(',', '.')) === formatNumber(oldValue?.replace(',', '.'))
      case 'status':
        return newValue === oldValue
      default:
        return false
    }
  }

  function handleTrackChanges(key, value, indexRow, title, indexPlaced) {
    const farmerTempTrading = cloneDeep(farmerGroupTradingData)
    const idTradingMonth = farmerGroupTradingData[indexPlaced].tradingMonths[indexRow].id
    const indexChange = trackChanges.findIndex(trackChange => trackChange.id === idTradingMonth && key === trackChange.key)
    const oldValue = indexChange >= 0 ? trackChanges[indexChange]?.oldValue : farmerGroupTradingData[indexPlaced]?.tradingMonths[indexRow][key]
    const oldValueFormated = getValue(key, oldValue)
    const newValue = getValue(key, value)
    const isEqual = isOldEqualNewValue(value, oldValue, key)
    if (isEqual) {
      setTrackChanges(trackChanges.filter(trackChange => trackChange.id !== idTradingMonth))
    } else if (indexChange >= 0) {
      const tempTrackChange = cloneDeep(trackChanges)
      tempTrackChange[indexChange].newValue = getValue(key, value)
      setTrackChanges(tempTrackChange)
    } else {
      const rowOfChange = {
        title,
        key,
        number: trackChanges.length + 1,
        id: idTradingMonth,
        farmerGroup: showData.farmerGroups[indexPlaced].name,
        oldValue: oldValueFormated,
        newValue: newValue,
        tradingMonth: farmerGroupTradingData[indexPlaced].tradingMonths[indexRow].date
      }
      setTrackChanges([...trackChanges, rowOfChange])
    }

    farmerTempTrading[indexPlaced].tradingMonths[indexRow][key] = value
    setFarmerGroupTradingData(farmerTempTrading)
  }

  function handleChangeInput(indexRow, key, valueInput, title) {
    const value = valueInput || ''

    const idToShow = showData.farmerGroups[activeTabIndex - 1].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.show.id === idToShow)

    handleTrackChanges(key, value, indexRow, title, indexPlaced)
  }

  function getInput(index, key, value, disabledRow, title) {
    const statusValue = farmerGroupTradingData[activeTabIndex - 1]?.tradingMonths[index].status
    const liveStatus = showData?.status === 'active'
    const disableEditing = key === 'percent' ? roleSlug !== ADMIN_ROLE_SLUG : disableActionRole
    if (liveStatus) {
      return formatNumber(value)
    } else {
      return <CustomInput
        value={value}
        allowDecimals
        placeholder=''
        error={disabledRow ? false : !value || value === '' || value === 0}
        onChange={(value) => handleChangeInput(index, key, value, title)}
        disabled={isTradingSlotExpired ? isTradingSlotExpired : statusValue === 0 || disableEditing}
      />
    }
  }

  function renderCellBasedOnType(tableColumn, tradingSlot, index) {
    const { key, format, type, title } = tableColumn
    const value = tradingSlot[key]
    switch (type) {
      case 'price-total':
        return value ? `${formatNumber(parseFloat(value))} €` : '0,00 €'
      case 'no-unit-number':
        return value ? `${formatNumberWithoutFractions(parseFloat(value).toFixed(2))}` : '0'
      case 'dateRibbon':
        return <Label ribbon>{dayjs(value).format(format)}</Label>
      case 'number':
        return `${formatNumberWithoutFractions(value)} ${amountUnit}`
      case 'label':
        return <Label ribbon>{value}</Label>
      case 'input':
        return getInput(index, key, value, tradingSlot?.status === 0, title)
      case 'icon':
        return key === 'status' ? renderStatusCellValue(value, index) : <Icon
          color={value === 0 ? 'red' : 'green'}
          name={value === 0 ? 'close' : 'checkmark'}
        />
      case 'date':
        return dayjs(value).format(format)
    }
  }

  function getTableCell(tableColumn, tradingSlot, index) {
    const value = tradingSlot[tableColumn.key]
    const cellValue = tableColumn?.type ? renderCellBasedOnType(tableColumn, tradingSlot, index) : value
    return <Table.Cell key={tableColumn.key}>{cellValue}</Table.Cell>
  }

  function getActiveRow(tradingSlot) {
    return isTradingSlotExpired ? true : tradingSlot.status === 0
  }

  function tableBody(column, rows) {
    return <Table.Body>
      {rows.map((tradingSlot, index) => {
        return <Table.Row active={getActiveRow(tradingSlot)} key={tradingSlot.id}>
          {column.map(tableColumn => {
            return getTableCell(tableColumn, tradingSlot, index)
          })}
        </Table.Row>
      })}

    </Table.Body>
  }

  function tableHeaders(columns) {
    return <Table.Row>
      {columns.map(tableColumn => {
        return <Table.HeaderCell key={tableColumn.title}>{tableColumn.title}</Table.HeaderCell>
      })}
    </Table.Row>
  }

  function renderTable(columns, rows) {
    return <Table padded>
      <Table.Header>
        {tableHeaders(columns)}
      </Table.Header>
      {tableBody(columns, rows)}
    </Table>
  }

  function handleClickFarmerGroupAccordion(e, titleProps, indexFarmer) {
    const { index } = titleProps
    const tempFarmerGroupTradingData = [...farmerGroupTradingData]
    tempFarmerGroupTradingData[indexFarmer].activeIndex = tempFarmerGroupTradingData[indexFarmer].activeIndex === index ? -1 : index
    setFarmerGroupTradingData(tempFarmerGroupTradingData)
  }

  async function handleSaveFarmerGroupTradingMonth() {
    const idFarmer = showData.farmerGroups[activeTabIndex - 1].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.show.id === idFarmer)
    const { tradingMonths } = farmerGroupTradingData[indexPlaced]
    setIsBusy(true)
    try {
      tradingMonths.forEach(tradingMonth => {
        if (tradingMonth?.price?.includes(',')) {
          tradingMonth.price = tradingMonth.price.replace(',', '.')
        }
        if (tradingMonth.percent.includes(',')) {
          tradingMonth.percent = tradingMonth.percent.replace(',', '.')
        }
      })
      await StockExChangeService.updateShowTradingSlotFarmerDataMonths(id, idFarmer, { tradingMonths })
      const farmerTemp = cloneDeep(farmerGroupTradingData)
      const responseMonths = await StockExChangeService.showTradingSlotFarmerDataMonths(id, idFarmer)
      farmerTemp[indexPlaced].tradingMonths = responseMonths.data
      setFarmerGroupTradingData(farmerTemp)
      notify(t('Stock exchange months saved'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderDetailsTab() {
    return <ShowInfo
      showData={showData}
      activeIndex={0}
      onCompleteTimer={() => fetchShowData()}
    />
  }

  function getModalTriger() {
    return <Button
      primary
      size='small'
    >{t('Preview')}</Button>
  }

  function getColumnChangeValue(value, key) {
    switch (key) {
      case 'status':
        return value ? t('Enabled') : t('Disabled')
      default:
        return `${value} ${getUnit(key)}`
    }
  }

  function renderColumnChange(type, value, row) {
    return <ChangesWrapper>
      <Label as='a' color='red' image>
        {getColumnChangeValue(row?.oldValue, row?.key)}
      </Label>
      <Icon name='arrow alternate circle right outline' />
      <Label as='a' color='green' image>
        {getColumnChangeValue(row?.newValue, row?.key)}
      </Label>
    </ChangesWrapper>
  }

  function renderFarmerGroup() {
    const idToShow = showData.farmerGroups[activeTabIndex - 1].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.show.id === idToShow)
    const activeIndex = farmerGroupTradingData[indexPlaced]?.activeIndex
    const hideFarmers = operativeUserLogged ? operativeUsersCanSeeFarmersInfo === 0 : false
    const tableHeaders = showData?.displayIfe ? tradingMonthsTableHeaders : tradingMonthsTableHeaders.filter(column => column.key !== 'ifePrice')
    const actionsDisplay = Boolean(PRICE_CHANGE_STATUS_SLUGS.find(slug => slug === showData?.status)) && !disableActionRole
    
    return <>
      {actionsDisplay && <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
        <Button
          primary
          size='small'
          onClick={() => handleSaveFarmerGroupTradingMonth()}
        >{t('Update')}</Button>
        <Dialog
          open={openModal}
          onOpen={() => setOpenModal(true)}
          handleClose={() => setOpenModal(false)}
          onClose={() => setOpenModal(false)}
          trigger={getModalTriger()}
          textContent={<TableComponent
            tableColumnHeaders={columnsChangePreview}
            rows={trackChanges}
            renderColumn={(type, value, row) => renderColumnChange(type, value, row)}
            removePagination
            total={trackChanges.length}
          />}
          textHeader={t('Stock exchange / Changes preview')}
          type={'dialog'}
        />
      </div>
      }
      {!isBusy && <ShowInfo
        tradingVolumeType={`${showData?.tradingVolumeTypeName} (${formatNumber(showData?.tradingVolumeTypePercent)} %)`}
        showData={{ ...farmerGroupTradingData[indexPlaced]?.show, displayIfe: showData?.displayIfe }}
        activeIndex={activeTabIndex}
      />
      }
      {farmerGroupTradingData[indexPlaced] && <StyledAccordion styled>
        <Accordion.Title
          onClick={(e, data) => handleClickFarmerGroupAccordion(e, data, indexPlaced)}
          index={0}
          active={activeIndex === 0}
        >
          <div>{t('Trading Months')}</div>
          <Icon name={activeIndex === 0 ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition visible={activeIndex === 0} animation='slide down' duration={200}>
          <Accordion.Content
            active={activeIndex === 0}
            content={renderTable(tableHeaders, farmerGroupTradingData[indexPlaced]?.tradingMonths)}
          />
        </Transition>
      </StyledAccordion>
      }
      {farmerGroupTradingData[indexPlaced]?.farmers && !hideFarmers && <FarmersTradingTable farmersTrading={farmerGroupTradingData[indexPlaced]?.farmers} farmerGroupId={farmerGroupTradingData[indexPlaced]?.show?.id}/>}
    </>
  }

  async function handleTabChange(activeTabIndex) {
    setActiveTabIndex(activeTabIndex)
    const idToShow = showData.farmerGroups[activeTabIndex - 1].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.show.id === idToShow)
    const hideFarmers = operativeUserLogged ? operativeUsersCanSeeFarmersInfo === 0 : false
    if (indexPlaced < 0) {
      setIsBusy(true)
      try {
        const farmerGroupId = showData.farmerGroups[activeTabIndex - 1].id
        const response = await StockExChangeService.showTradingSlotFarmerData(id, farmerGroupId)
        const responseMonths = await StockExChangeService.showTradingSlotFarmerDataMonths(id, farmerGroupId)
        const farmersResponse = hideFarmers ? { data: [] } : await StockExChangeService.farmersTradingSlot(id, { farmerGroupId: farmerGroupId })
        setFarmerGroupTradingData([...farmerGroupTradingData, {
          show: response.data,
          tradingMonths: responseMonths.data,
          activeIndex: -1,
          farmers: farmersResponse.data
        }
        ])
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderShowData() {
    const farmerGroupTabs = showData.farmerGroups.map(farmerGroup => {
      return {
        menuItem: farmerGroup.name,
        render: () => renderFarmerGroup()
      }
    })
    const panes = [
      {
        menuItem: t('Details'),
        render: () => renderDetailsTab()
      },
      ...farmerGroupTabs
    ]

    return <Tab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeTabIndex}
      onTabChange={(e, data) => handleTabChange(data.activeIndex)}
      panes={panes}
    />
  }

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Stock exchange')}
    actions={!disableActionRole && renderActions()}
    close={`${RouteTypes.STOCK_EXCHANGE}#${showData?.status}`}
  >
    {showData && renderShowData()}
    <PlanModal
      slotType='stockExchange'
      openDialog={openPlanModal}
      handleClose={(submited) => {
        if (submited) {
          fetchShowData()
        }
        setOpenPlanModal(false)
      }}
      showData={showData}
    />
    <NotifyModal
      openModal={openNotifyModal}
      handleClose={() => setOpenNotifyModal(false)}
      showData={showData}
    />
  </Layout>
}
export default ShowStockExchange
